/**
 * FlowToolbar.tsx
 * Top toolbar for running and clearing logs.
 */
import {
  Bot,
  CircleHelp,
  CodeXml,
  Link2,
  MonitorPlay,
  Redo2,
  RotateCcw,
  Square,
  Trash2,
  Undo2,
} from 'lucide-react';
import type { ReactNode, Ref } from 'react';
import type { FlowDisplayLanguage } from '../displayLanguage';

export type FlowRunMode = 'simulation' | 'robots';

type FlowToolbarProps = {
  historyControl: {
    canUndo: boolean;
    canRedo: boolean;
    onUndo: () => void;
    onRedo: () => void;
  };
  runControl: {
    isRunning: boolean;
    activeMode: FlowRunMode | null;
    simulationDisabled: boolean;
    robotsDisabled: boolean;
    connectedCount: number;
    targetCount: number;
    onRunSimulation: () => void;
    onRunRobots: () => void;
    onStop: () => void;
  };
  connectControl?: {
    enabled: boolean;
    onToggle: () => void;
  };
  deleteControl?: {
    enabled: boolean;
    onDelete: () => void;
  };
  resetControl?: {
    disabled: boolean;
    onReset: () => void;
  };
  guideControl?: {
    onOpen: () => void;
  };
  sourceControl?: {
    onOpen: () => void;
    buttonRef?: Ref<HTMLButtonElement>;
  };
  mobileControls?: {
    isPaletteOpen: boolean;
    isInspectorOpen: boolean;
    onTogglePalette: () => void;
    onToggleInspector: () => void;
  };
  brandSlot?: ReactNode;
  trailingSlot?: ReactNode;
  languageControl?: {
    displayLanguage: FlowDisplayLanguage;
    onToggle: () => void;
  };
};

export function FlowToolbar({
  historyControl,
  runControl,
  connectControl,
  deleteControl,
  resetControl,
  guideControl,
  sourceControl,
  mobileControls,
  brandSlot,
  trailingSlot,
  languageControl,
}: FlowToolbarProps) {
  return (
    <div className="flow-toolbar relative flex items-center gap-2 border-b border-border bg-card/40 px-3 py-1.5">
      {brandSlot}
      <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
        {mobileControls && (
          <div className="flex shrink-0 items-center gap-2">
            <button
              onClick={mobileControls.onTogglePalette}
              className={`h-9 shrink-0 rounded-lg border px-3 text-sm font-medium transition-colors ${
                mobileControls.isPaletteOpen
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-background hover:bg-muted'
              }`}
            >
              カード
            </button>
            <button
              onClick={mobileControls.onToggleInspector}
              className={`h-9 shrink-0 rounded-lg border px-3 text-sm font-medium transition-colors ${
                mobileControls.isInspectorOpen
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-background hover:bg-muted'
              }`}
            >
              説明/ログ
            </button>
          </div>
        )}
      </div>
      <div data-product-tour="flow-run" className="flow-toolbar__primary-actions">
        <div className="flow-toolbar__action-group" role="group" aria-label="編集操作">
          <div className="flow-toolbar__primary-item">
            <button
              type="button"
              onClick={historyControl.onUndo}
              disabled={!historyControl.canUndo}
              aria-label="元に戻す"
              title="元に戻す (Ctrl+Z)"
              className="flow-toolbar__primary-button"
            >
              <Undo2 aria-hidden="true" />
            </button>
            <span className="flow-toolbar__primary-label">元に戻す</span>
          </div>
          <div className="flow-toolbar__primary-item">
            <button
              type="button"
              onClick={historyControl.onRedo}
              disabled={!historyControl.canRedo}
              aria-label="やり直す"
              title="やり直す (Ctrl+Shift+Z)"
              className="flow-toolbar__primary-button"
            >
              <Redo2 aria-hidden="true" />
            </button>
            <span className="flow-toolbar__primary-label">やり直す</span>
          </div>
          {(connectControl || deleteControl) && (
            <div data-product-tour="flow-edit" className="flow-toolbar__edit-actions">
              {connectControl && (
                <div className="flow-toolbar__primary-item">
                  <button
                    type="button"
                    onClick={connectControl.onToggle}
                    aria-label={connectControl.enabled ? 'つなぐモードを終了' : 'つなぐモードを開始'}
                    title={connectControl.enabled ? 'つなぐモードを終了' : 'つなぐ'}
                    className={`flow-toolbar__primary-button${connectControl.enabled ? ' is-selected' : ''}`}
                  >
                    <Link2 aria-hidden="true" />
                    <span className="sr-only">{connectControl.enabled ? 'つなぐ中' : 'つなぐ'}</span>
                  </button>
                  <span className="flow-toolbar__primary-label">{connectControl.enabled ? 'つなぐ中' : 'つなぐ'}</span>
                </div>
              )}
              {deleteControl && (
                <div className="flow-toolbar__primary-item">
                  <button
                    type="button"
                    onClick={deleteControl.onDelete}
                    disabled={!deleteControl.enabled}
                    aria-label="ごみばこ"
                    title="選択したカードまたは線を削除"
                    className="flow-toolbar__primary-button flow-toolbar__primary-button--delete"
                  >
                    <Trash2 aria-hidden="true" />
                    <span className="sr-only">ごみばこ</span>
                  </button>
                  <span className="flow-toolbar__primary-label">ごみばこ</span>
                </div>
              )}
            </div>
          )}
          {resetControl && (
            <div className="flow-toolbar__primary-item">
              <button
                type="button"
                disabled={resetControl.disabled}
                onClick={resetControl.onReset}
                aria-label="はじめから"
                title="はじめから"
                className="flow-toolbar__primary-button"
              >
                <RotateCcw aria-hidden="true" />
              </button>
              <span className="flow-toolbar__primary-label">はじめから</span>
            </div>
          )}
        </div>
        <div className="flow-toolbar__action-group" role="group" aria-label="実行">
          <div className="flow-toolbar__primary-item">
            <button
              type="button"
              onClick={runControl.activeMode === 'simulation' ? runControl.onStop : runControl.onRunSimulation}
              disabled={runControl.isRunning ? runControl.activeMode !== 'simulation' : runControl.simulationDisabled}
              aria-label={runControl.activeMode === 'simulation' ? 'テストを停止' : 'テスト'}
              title={runControl.activeMode === 'simulation' ? 'テストを停止' : 'テスト'}
              className={`flow-toolbar__primary-button flow-toolbar__primary-button--simulation${runControl.activeMode === 'simulation' ? ' is-stopping' : ''}`}
            >
              {runControl.activeMode === 'simulation'
                ? <Square aria-hidden="true" />
                : <MonitorPlay aria-hidden="true" />}
              <span className="sr-only">{runControl.activeMode === 'simulation' ? '停止' : 'テスト'}</span>
            </button>
            <span className="flow-toolbar__primary-label">{runControl.activeMode === 'simulation' ? '停止' : 'テスト'}</span>
          </div>
          <div className="flow-toolbar__primary-item">
            <button
              type="button"
              onClick={runControl.activeMode === 'robots' ? runControl.onStop : runControl.onRunRobots}
              disabled={runControl.isRunning ? runControl.activeMode !== 'robots' : runControl.robotsDisabled}
              aria-label={runControl.activeMode === 'robots'
                ? '実機でのフロー実行を停止'
                : `接続中の実機${runControl.connectedCount}台でフローを実行`}
              title={runControl.activeMode === 'robots'
                ? '実機でのフロー実行を停止'
                : `実機（${runControl.connectedCount}台）`}
              className={`flow-toolbar__primary-button flow-toolbar__primary-button--robots${runControl.activeMode === 'robots' ? ' is-stopping' : ''}`}
            >
              {runControl.activeMode === 'robots'
                ? <Square aria-hidden="true" />
                : <Bot aria-hidden="true" />}
              <span className="sr-only">{runControl.activeMode === 'robots' ? '停止' : `実機（${runControl.connectedCount}台）`}</span>
            </button>
            <span className="flow-toolbar__primary-label">{runControl.activeMode === 'robots' ? '停止' : '実機'}</span>
          </div>
        </div>
        {guideControl && (
          <div className="flow-toolbar__action-group" role="group" aria-label="ヘルプ">
            <div className="flow-toolbar__primary-item">
              <button
                type="button"
                onClick={guideControl.onOpen}
                aria-label="メイン画面の使い方"
                title="使い方"
                className="flow-toolbar__primary-button"
              >
                <CircleHelp aria-hidden="true" />
              </button>
              <span className="flow-toolbar__primary-label">使い方</span>
            </div>
          </div>
        )}
        <span className="sr-only" aria-live="polite">
          {runControl.isRunning && (
            runControl.activeMode === 'simulation'
              ? 'テスト実行中'
              : `実機 ${runControl.targetCount}台で実行中`
          )}
        </span>
      </div>
      {sourceControl && (
        <div className="flow-toolbar__source-action">
          <button
            ref={sourceControl.buttonRef}
            type="button"
            onClick={sourceControl.onOpen}
            aria-label="ソースを表示"
            title="ソースを表示"
            className="workspace-directory-controls__button"
          >
            <CodeXml aria-hidden="true" />
            <span className="workspace-directory-controls__label">ソース</span>
          </button>
        </div>
      )}
      {languageControl && (
        <button
          type="button"
          onClick={languageControl.onToggle}
          aria-label={`表示言語を${languageControl.displayLanguage === 'en' ? '日本語' : 'English'}に切り替え`}
          title={languageControl.displayLanguage === 'en' ? '日本語表示に切り替え' : 'Switch to English'}
          className="h-9 shrink-0 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
        >
          {languageControl.displayLanguage === 'en' ? '日本語' : 'English'}
        </button>
      )}
      {trailingSlot}
    </div>
  );
}
